import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getInstructor, updateInstructor, deleteInstructor } from '../../../api/adminApi';
import Badge from '../../../components/admin/Badge';
import Toast from '../../../components/admin/Toast';
import ConfirmModal from '../../../components/admin/ConfirmModal';
import { toTitleCase } from '../../../utils/format';

const AV_COLORS = ['#5B6ABF','#1A7048','#E8440A','#D4882A','#8A6FB0','#2E8B8B','#C74D3A','#F7C91B'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export default function InstructorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data,      setData]      = useState(null);
  const [form,      setForm]      = useState({});
  const [editModal, setEditModal] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState(null);
  const [showDelete,setShowDelete]= useState(false);

  const load = () =>
    getInstructor(id).then(d => {
      setData(d);
      setForm({
        full_name:  d.instructor.full_name,
        department: d.instructor.department || '',
      });
    }).catch(console.error);

  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="body" style={{ color: 'var(--muted)', padding: 40 }}>Loading…</div>;

  const { instructor, courses } = data;
  const color = avColor(instructor.id);
  const joined = fmtDate(instructor.joined_at);

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Instructors / {toTitleCase(instructor.full_name)}</div>
          <div className="pt">{toTitleCase(instructor.full_name)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-o btn-sm" onClick={() => navigate('/admin/instructors')}>← All Instructors</button>
          <button className="btn btn-d btn-sm" onClick={() => setShowDelete(true)}>🗑 Delete</button>
        </div>
      </div>

      <div className="body">

        {/* ── Profile Header ── */}
        <div className="profile-header" style={{ animation: 'slideUp .3s ease' }}>
          <div className="av-lg" style={{ background: color, color: 'white' }}>
            {initials(instructor.full_name)}
          </div>
          <div className="profile-info">
            <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 800, fontSize: 20, marginBottom: 2 }}>
              {toTitleCase(instructor.full_name)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
              {instructor.email}{instructor.department ? ` · ${instructor.department}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span className={`b ${instructor.status === 'Active' ? 'b-g' : 'b-gr'}`}>
                {instructor.status || 'Active'}
              </span>
              {joined && <span className="b b-y">Joined {joined}</span>}
              <span className="b b-o">{courses.length} course{courses.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <button className="btn btn-o btn-sm" onClick={() => setEditModal(true)}>Edit Profile</button>
        </div>

        {/* ── Assigned Courses ── */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="ct">
            <span>Assigned Courses</span>
          </div>
          {courses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 28, color: 'var(--muted)', fontSize: 13 }}>
              No courses assigned yet.
            </div>
          ) : (
            courses.map(c => (
              <div
                key={c.id}
                className="mi"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/admin/courses/${c.id}`)}
              >
                {/* Colored emoji square */}
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: `${c.accent_color || '#5B6ABF'}22`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}>
                  {c.emoji || '📚'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mt">{c.title}</div>
                  <div className="ms">{c.enrolled_count} student{c.enrolled_count !== 1 ? 's' : ''} · {c.modules_total} module{c.modules_total !== 1 ? 's' : ''}</div>
                </div>
                <Badge status={c.status} />
              </div>
            ))
          )}
        </div>

      </div>

      {/* ── Edit Profile Modal ── */}
      {editModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              Edit Instructor
              <button className="btn btn-o btn-sm" onClick={() => setEditModal(false)}>✕</button>
            </div>
            <div className="fg">
              <label className="fl">Full Name</label>
              <input className="li" placeholder="e.g. Dr. Jane Smith" value={form.full_name || ''}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="fg">
              <label className="fl">Department</label>
              <input className="li" placeholder="e.g. Data Science" value={form.department || ''}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))} />
            </div>
            <div className="fg" style={{ marginBottom: 0 }}>
              <label className="fl">Email</label>
              <input className="li" value={instructor.email} readOnly
                style={{ background: '#f8f9fa', color: 'var(--muted)', cursor: 'not-allowed' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-o" onClick={() => setEditModal(false)}>Cancel</button>
              <button className="btn btn-p" disabled={saving} onClick={async () => {
                setSaving(true);
                try {
                  await updateInstructor(id, form);
                  setEditModal(false);
                  setToast('Profile updated');
                  load();
                } catch (err) {
                  setToast({ msg: err.message, type: 'error' });
                } finally { setSaving(false); }
              }}>
                {saving ? 'Saving…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}

      {showDelete && (
        <ConfirmModal
          title="Delete Instructor?"
          message="This instructor will be removed from the platform. Their courses will remain but become unassigned."
          confirmLabel="Yes, Delete"
          onCancel={() => setShowDelete(false)}
          onConfirm={async () => {
            try { await deleteInstructor(instructor.id); navigate('/admin/instructors'); }
            catch (err) { setToast({ msg: err.message, type: 'error' }); setShowDelete(false); }
          }}
        />
      )}
    </>
  );
}
